import { Injectable } from '@angular/core';
import { StateCreator, StoreApi } from 'zustand/vanilla';
import { ProvisionRequest } from '../../api/provision-request-api.class';
import { PersonApiService } from '../../api/person-api.service';
import { firstValueFrom, forkJoin, lastValueFrom, Observable } from 'rxjs';
import { Locker } from '../../api/locker-api.class';
import { GetLockersInput } from '../../api/locker-api.types';
import { LockerApiService } from '../../api/locker-api.service';
import { PersonModel } from '../../shared/person.model';
import { ProvisionRequestApiService } from '../../api/provision-request-api.service';
import { GetPersonOutput } from '../../api/person-api.types';
import { GetProvisionRequestsInput } from '../../api/provision-request-api.types';

export interface ProvisionRequestsState {
  activeProvisionRequest: ProvisionRequest | null;
  activeProvisionRequestPersons: PersonModel[] | [];
  activeProvisionRequestLockerSuggestions: Locker[] | [];
  activeProvisionRequests: ProvisionRequest[];
}

export interface ProvisionRequestsSlice {
  provisionRequests: ProvisionRequestsState;
}

export interface ProvisionRequestsActions {
  setProvisionRequests: (params?: GetProvisionRequestsInput) => Promise<void>;
  updateActiveProvisionRequest: (provisionRequest: ProvisionRequest) => void;
  getActiveProvisionRequest: () => ProvisionRequest | null;
  setActiveProvisionRequestPersons: () => Promise<void>;
  setActiveProvisionRequestLockerSuggestions: (
    input: GetLockersInput,
  ) => Promise<void>;
  updateActiveProvisionRequestPersons: (
    activeProvisionRequestPersons: PersonModel[],
  ) => void;
  updateActiveProvisionRequestPerson: (
    activeProvisionRequestPerson: PersonModel,
  ) => void;
  updateClothesOnProvisionPerson: (id: number) => void;
  reset: () => void;
}

const initialProvisionsState: ProvisionRequestsState = {
  activeProvisionRequest: null,
  activeProvisionRequestPersons: [],
  activeProvisionRequestLockerSuggestions: [],
  activeProvisionRequests: [],
};

@Injectable()
export class ProvisionRequestsStoreService {
  private slice: StateCreator<ProvisionRequestsSlice>;
  private actions: (
    store: StoreApi<ProvisionRequestsSlice>,
  ) => ProvisionRequestsActions;

  constructor(
    provisionRequestApiService: ProvisionRequestApiService,
    personApiService: PersonApiService,
    lockerApiService: LockerApiService,
  ) {
    this.slice = () => ({
      provisionRequests: initialProvisionsState,
    });

    this.actions = (store) => ({
      async setProvisionRequests(params) {
        const queryParams = params || {};

        const result = await firstValueFrom(
          provisionRequestApiService.getAllProvisionRequests(queryParams),
        );
        const transformed = ProvisionRequest.fromResultArray(result);

        // Check if params is an empty object
        const isEmptyParams = Object.keys(params || {}).length === 0;
        // Params is only not empty if accessed from dashboard
        // If accessed from dashboard
        // we dont want closed requests to be displayed
        const activeProvisionRequests = !isEmptyParams
          ? transformed.filter((request) => request.beendetAm === null)
          : transformed;

        store.setState((state) => ({
          provisionRequests: {
            ...state.provisionRequests,
            activeProvisionRequests: activeProvisionRequests || [],
          },
        }));
      },

      updateActiveProvisionRequest(provisionRequest) {
        store.setState((state) => ({
          provisionRequests: {
            ...state.provisionRequests,
            activeProvisionRequest: provisionRequest,
          },
        }));
      },

      getActiveProvisionRequest() {
        return store.getState().provisionRequests.activeProvisionRequest;
      },

      async setActiveProvisionRequestPersons() {
        const personIds =
          store.getState().provisionRequests.activeProvisionRequest?.personen;
        if (personIds) {
          const personObservables: Observable<GetPersonOutput>[] =
            personIds.map((id) => personApiService.getPerson(id));

          const persons = await lastValueFrom(forkJoin(personObservables));
          const activeProvisionRequestPersons: PersonModel[] = persons.map(
            (person) => {
              let geschlecht = '';
              if (person.geschlecht === 1) {
                geschlecht = 'Männlich';
              } else if (person.geschlecht === 2) {
                geschlecht = 'Weiblich';
              } else if (person.geschlecht === 3) {
                geschlecht = 'Divers';
              }

              const spind = person.spinde.filter(
                (locker) =>
                  locker.spindartId === 1 ||
                  locker.spindartId === 2 ||
                  locker.spindartId === 3,
              );
              return new PersonModel(
                person.id,
                geschlecht,
                person.vorname,
                person.name,
                person.bemerkung,
                person.gruppe,
                spind[0]?.spindId ?? null,
                spind[0]?.bezeichnung ?? null,
                person.firma.bezeichnung,
                person.firmaId,
                spind[0] ? 'Zugewiesen' : 'Ausstehend',
                person.groesse_hemd,
                person.hemd_inventarnummer,
                person.groesse_hose,
                person.hose_inventarnummer,
                person.groesse_jacke,
                person.jacke_inventarnummer,
                person.groesse_kittel,
                person.kittel_inventarnummer,
                person.groesse_schuhe,
                person.schuhe_inventarnummer,
              );
            },
          );

          store.setState((state) => ({
            provisionRequests: {
              ...state.provisionRequests,
              activeProvisionRequestPersons,
            },
          }));
        }
      },

      updateActiveProvisionRequestPersons(
        activeProvisionRequestPersons: PersonModel[],
      ) {
        activeProvisionRequestPersons.sort((a, b) =>
          a.vorname.localeCompare(b.vorname),
        );

        store.setState((state) => ({
          provisionRequests: {
            ...state.provisionRequests,
            activeProvisionRequestPersons,
          },
        }));
      },

      updateActiveProvisionRequestPerson(
        activeProvisionRequestPerson: PersonModel,
      ) {
        const activeProvisionRequestPersons = [activeProvisionRequestPerson];
        const persons =
          store.getState().provisionRequests.activeProvisionRequestPersons;
        persons.forEach((person) => {
          if (person.id !== activeProvisionRequestPerson.id) {
            activeProvisionRequestPersons.push(person);
          }
        });
        activeProvisionRequestPersons.sort((a, b) =>
          a.vorname.localeCompare(b.vorname),
        );
        store.setState((state) => ({
          provisionRequests: {
            ...state.provisionRequests,
            activeProvisionRequestPersons,
          },
        }));
      },

      async setActiveProvisionRequestLockerSuggestions(input: GetLockersInput) {
        const activeProvisionRequestLockerSuggestions = Locker.fromResultArray(
          await lastValueFrom(lockerApiService.searchLocker(input)),
        );

        store.setState((state) => ({
          provisionRequests: {
            ...state.provisionRequests,
            activeProvisionRequestLockerSuggestions,
          },
        }));
      },

      async updateClothesOnProvisionPerson(id: number) {
        const updatedPerson = await firstValueFrom(
          personApiService.getPerson(id),
        );

        const persons =
          store.getState().provisionRequests.activeProvisionRequestPersons;

        const updatedPersons = persons.map((person) => {
          if (person.id === id) {
            // Update only clothing-related fields
            return {
              ...person,
              groesseHemd: updatedPerson.groesse_hemd,
              hemdInventarnummer: updatedPerson.hemd_inventarnummer,
              groesseHose: updatedPerson.groesse_hose,
              hoseInventarnummer: updatedPerson.hose_inventarnummer,
              groesseJacke: updatedPerson.groesse_jacke,
              jackeInventarnummer: updatedPerson.jacke_inventarnummer,
              groesseKittel: updatedPerson.groesse_kittel,
              kittelInventarnummer: updatedPerson.kittel_inventarnummer,
              groesseSchuhe: updatedPerson.groesse_schuhe,
              schuheInventarnummer: updatedPerson.schuhe_inventarnummer,
            };
          }
          return person;
        });

        store.setState((state) => ({
          provisionRequests: {
            ...state.provisionRequests,
            activeProvisionRequestPersons: updatedPersons,
          },
        }));
      },

      reset() {
        store.setState({ provisionRequests: initialProvisionsState });
      },
    });
  }

  getSlice() {
    return this.slice;
  }

  getActions() {
    return this.actions;
  }
}
